// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AddressInfoDisplay from "@bg/Overrides/Components/AddressInfoDisplay";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getOrderPromotions,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, CreditCardBillingAddressDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    // XNG-Chnages
    const promotions = getPromotionsDataView(state, state.pages.onePageCheckoutConfirmation.cartId).value;
    let orderPromotions;
    if (promotions) {
        orderPromotions = getOrderPromotions(promotions);
    }
    return {
        cart,
        // XNG-Changes
        billingAddress: getBillToState(state, cart?.billToId).value,
        orderPromotions,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationPaymentMethodStyles {
    container?: InjectableCss;
    paymentMethodTitleText?: TypographyPresentationProps;
    paymentMethodText?: TypographyPresentationProps;
    // XNG-change
    title?: TypographyPresentationProps;
    innerContainer?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    billingAddress?: TypographyPresentationProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const paymentMethodStyles: OnePageCheckoutConfirmationPaymentMethodStyles = {
    container: {
        css: css`
            flex-direction: column;
            margin-top: 15px;
        `,
    },
    paymentMethodTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = paymentMethodStyles;

const isSame = (
    creditCardBillingAddress: CreditCardBillingAddressDto,
    billingAddress: BillToModel = {} as BillToModel,
) => {
    return (
        creditCardBillingAddress.address1 === billingAddress.address1 &&
        creditCardBillingAddress.address2 === billingAddress.address2 &&
        creditCardBillingAddress.city === billingAddress.city &&
        creditCardBillingAddress.countryAbbreviation === billingAddress.country?.abbreviation &&
        creditCardBillingAddress.stateAbbreviation === billingAddress.state?.abbreviation &&
        creditCardBillingAddress.postalCode === billingAddress.postalCode
    );
};

const OnePageCheckoutConfirmationPaymentMethod = ({
    cart,

    // XNG-Changes
    billingAddress,
    orderPromotions,
}: Props) => {
    if (!cart) {
        return null;
    }

    // XNG-Changes
    const promotionCodes = orderPromotions?.filter(o => o.amount > 0).map(o => o.promotionCode);

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{translate("Payment")}</Typography>
            <StyledWrapper {...styles.innerContainer}>
                <StyledWrapper {...styles.leftSection}>
                    {!!(cart.paymentMethod?.description || cart.paymentMethod?.name) && (
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Payment Method")}
                            text={cart.paymentMethod?.description || cart.paymentMethod?.name}
                        />
                    )}
                    {cart.properties.creditCardTransaction && cart.paymentMethod?.isCreditCard && (
                        <Typography {...styles.paymentMethodText}>{cart.properties.creditCardTransaction}</Typography>
                    )}
                    {!!cart.creditCardBillingAddress && !isSame(cart.creditCardBillingAddress, billingAddress) && (
                        <StyledWrapper>
                            <Typography {...styles.billingAddress}>
                                {translate("Credit Card Billing Address")}
                            </Typography>
                            <AddressInfoDisplay
                                address1={cart.creditCardBillingAddress.address1}
                                address2={cart.creditCardBillingAddress.address2}
                                country={cart.creditCardBillingAddress.countryAbbreviation}
                                state={cart.creditCardBillingAddress.stateAbbreviation}
                                postalCode={cart.creditCardBillingAddress.postalCode}
                                city={cart.creditCardBillingAddress.city}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                {promotionCodes && promotionCodes?.length > 0 && (
                    <StyledWrapper {...styles.rightSection}>
                        <SmallHeadingAndText
                            heading={translate("Promo Code:")}
                            text={promotionCodes.join(", ")}
                            extendedStyles={styles.headingAndTextStyles}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationPaymentMethod),
    definition: {
        displayName: "Payment Method",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
